import {
  AdgroupFormData,
  CampaignFormData,
  InterestBehaviorScene,
  InterestBehavior,
  TargetingKeywordsType,
  InterestBehaviorTab,
  TargetingKeywordsSearchStatus,
  InterestBehaviorData,
  VIDEO_OFFLINE_TAG_ID,
} from '@tt4b/ttam-enums';
import { Ref, computed } from '@vue/composition-api';
import { cloneDeep, uniqBy } from 'lodash-es';
import { useInterestBehaviorApi, useInterstKeywordsInfoApi, useBehaviorHashTagsInfoApi } from '@tt4b/ttam-hooks';
import { walkTreeBFS, setParent } from '@tt4b/ttam-utils';
import { formatHashtagLabel, generateId, getIdItemMap } from './../../../../utils/index';
import { i18n } from './../../../../i18n';
import store from './store';
const { state, mutations } = store;
// 显示顺序
const DisplayOrder = [
  InterestBehaviorScene.INTEREST,
  InterestBehaviorScene.INMARKET,
  InterestBehaviorScene.VIDEO,
  InterestBehaviorScene.CREATOR,
  InterestBehaviorScene.HASHTAG,
];

// 全量支持且有单独分类的scene
const BaseScenes = [
  InterestBehaviorScene.INTEREST,
  InterestBehaviorScene.VIDEO,
  InterestBehaviorScene.CREATOR,
  InterestBehaviorScene.HASHTAG,
  InterestBehaviorScene.INMARKET,
];

// 格式hashtag的label
export function formatHashtagLabelName(tagType, tagName) {
  return tagType === TargetingKeywordsType.HASHTAG_GROUP
    ? i18n.t('adgroup_target_hashtag_search_hashtaggroup', { name: tagName })
    : formatHashtagLabel(tagName);
}

// 根据不同的sence保存
function saveCategoryList(scene, data) {
  const _data = cloneDeep(data);
  walkTreeBFS({
    root: { children: _data },
    handler: node => {
      node.id = node.tag_id;
      node.label = node.tag_name;
      node.i18n_value = node.tag_name;
    },
  });
  switch (Number(scene)) {
    case InterestBehaviorScene.VIDEO:
      mutations.setVideoBehaviorList(_data);
      break;
    case InterestBehaviorScene.CREATOR:
      mutations.setCreatorBehaviorList(_data);
      break;
    case InterestBehaviorScene.INTEREST:
      mutations.setInterestList(_data);
      break;
    default:
      break;
  }
}

// 格式化节点格式
export function formatCategoryList(
  category,
  scene,
  props = {
    tag_id: 'tag_id',
    tag_name: 'tag_name',
    status: 'status',
  },
  visible = true,
) {
  const currentCategoryList = cloneDeep(category);
  walkTreeBFS({
    root: { children: currentCategoryList },
    handler: node => {
      node.tag_id = node[props.tag_id];
      node.scene = scene;
      node.tag_type = node.tag_type || node.typeEnum;
      node.label = node[props.tag_name];
      node.tag_name =
        scene === InterestBehaviorScene.HASHTAG
          ? formatHashtagLabelName(node.tag_type, node[props.tag_name])
          : node[props.tag_name];
      node.id = generateId(scene, node[props.tag_id]);
      node.type = InterestBehaviorTab()[scene].title;
      node.visible = visible && !(scene === InterestBehaviorScene.VIDEO && node.tag_id === VIDEO_OFFLINE_TAG_ID); // 跟后端确认是否是所有类目9都代表下线标签
      node.status = node[props.status] || TargetingKeywordsSearchStatus.ONLINE;
    },
  });

  return currentCategoryList;
}
/**
 * 用于添加搜索后被选择的tag(当前只有hashtag和interest keyword 搜索结果 > select tree list)到select tree list，且不在tree中展示
 * @param scene
 * @param payload
 */
function updatePanelDataByScene(
  scene: InterestBehaviorScene.HASHTAG | InterestBehaviorScene.INTEREST,
  payload: InterestBehaviorData[],
) {
  const panelData = cloneDeep(state.interestBehaviorList.value);
  const list = panelData.find(item => item.scene === scene) || { data: [] };
  list.data = uniqBy([...list.data, ...payload.map(item => ({ ...item, visible: false }))], 'id');
  mutations.setInterestBehaviorList(panelData);
  saveCategoryList(scene, list.data);
}

// 格式化返回的数据
function formatPanelData(category) {
  if (!Object.keys(category).length) {
    mutations.setInterestBehaviorList([]);
    return;
  }
  const list: InterestBehavior[] = cloneDeep(state.interestBehaviorList.value);
  const scenes = Object.keys(category).map(scene => Number(scene));
  scenes.forEach(scene => {
    const currentCategoryList = formatCategoryList(category[scene], scene);
    // 设置父子节点关系
    setParent({ children: currentCategoryList });

    const curSceneIndex = list.findIndex(item => item.scene === scene);
    if (curSceneIndex > -1) {
      list[curSceneIndex].data = currentCategoryList;
    } else {
      list.push({
        ...InterestBehaviorTab()[scene],
        data: currentCategoryList,
      });
    }
    saveCategoryList(scene, currentCategoryList);
  });

  list.sort((a, b) => DisplayOrder.indexOf(Number(a.scene)) - DisplayOrder.indexOf(Number(b.scene)));

  mutations.setInterestBehaviorList(list);
}
// 请求兴趣定向
export function useGetInterestBehaviorCategory(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { campaignData, adgroupData } = props;
  const scenes = BaseScenes;

  return {
    run: async () => {
      try {
        const category = await useInterestBehaviorApi({
          scenes, // 定向场景
          inventoryFlow: adgroupData.value.inventoryFlow, // 用户选择的版位
          industryTypes: campaignData.value.industryTypes, // 用户选择的行业
        });
        formatPanelData(category);
        mutations.setInterestBehaviorListParams(campaignData.value.industryTypes);
      } catch (error) {}
    },
  };
}

export function useInterestBehaviorStore() {
  const interestBehaviorListMap = computed(() => {
    const _interestBehaviorListMap = {};
    state.interestBehaviorList.value.forEach(list => {
      _interestBehaviorListMap[list.scene] = getIdItemMap(list.data, 'tag_id');
    });

    return _interestBehaviorListMap;
  });

  function updatePanelData(nodes) {
    const interestKeyword: InterestBehaviorData[] = [];
    const hashtag: InterestBehaviorData[] = [];
    nodes.forEach(item => {
      if (item.scene === InterestBehaviorScene.HASHTAG) {
        hashtag.push(item);
      }
      if (item.scene === InterestBehaviorScene.KEYWORD) {
        interestKeyword.push(item);
      }
    });
    // 搜索出的hashtag比展示的多
    updatePanelDataByScene(InterestBehaviorScene.HASHTAG, hashtag);

    // interest keyword 只支持搜索,interest keyword放在interest tab下
    updatePanelDataByScene(InterestBehaviorScene.INTEREST, interestKeyword);
  }
  return {
    interestBehaviorListMap,
    updatePanelData,
  };
}
// 获取hashtag的info，方便处理上下线
export function loadHashTagsInfo(hashtagCategoriesIds: number[]) {
  return new Promise((resolve, reject) => {
    if (!hashtagCategoriesIds.length) {
      resolve([]);
    } else {
      const ids = hashtagCategoriesIds.map(v => ({ id: v }));
      useBehaviorHashTagsInfoApi(ids)
        .then(({ keywords }) => {
          const list = formatCategoryList(
            keywords,
            InterestBehaviorScene.HASHTAG,
            {
              tag_id: 'id',
              tag_name: 'label',
              status: 'status',
            },
            false,
          );
          updatePanelDataByScene(InterestBehaviorScene.HASHTAG, list);
          resolve(list);
        })
        .catch(e => {
          resolve([]);
        });
    }
  });
}
// 获取兴趣词的信息
export function useGetInterestKeywordsInfo(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;

  const params = computed(() => ({
    keywordInfos: adgroupData.value.interestKeywordsI18n.map((keyword_id, idx) => ({
      keyword_id,
      lang: adgroupData.value.interestKeywordsLangI18n[idx],
    })),
    industryTypes: campaignData.value.industryTypes ?? [],
  }));
  return new Promise(resolve => {
    if (!adgroupData?.value?.interestKeywordsI18n?.length) {
      resolve([]);
    } else {
      useInterstKeywordsInfoApi(params.value)
        .then(result => {
          const list = formatCategoryList(
            result,
            InterestBehaviorScene.KEYWORD,
            {
              tag_id: 'keyword_id',
              tag_name: 'keyword',
              status: 'keyword_status',
            },
            false,
          );
          updatePanelDataByScene(InterestBehaviorScene.INTEREST, list);
          resolve(list);
        })
        .catch(e => {
          resolve([]);
        });
    }
  });
}
